import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { NgClass, NgFor, NgIf, UpperCasePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { HttpServiceService } from '../services/http-service.service';
import { NgxToastrService } from '../_services/ngx-toastr/ngx-toastr.service';
import { BreadcrumbComponent } from '../elements/breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-templates',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, NgFor, NgIf, NgClass, UpperCasePipe, BreadcrumbComponent],
  templateUrl: './templates.component.html',
  styleUrl: './templates.component.css',
})
export class TemplatesComponent implements OnInit, OnDestroy {
  @ViewChild('docPreviewFrame') docPreviewFrame!: ElementRef<HTMLIFrameElement>;
  @ViewChild('notifPreviewFrame') notifPreviewFrame!: ElementRef<HTMLIFrameElement>;

  breadcrumb = {
    breadcrumb_path: 'Dashboard',
    currentURL: 'Templates',
    breadcrumb_url: '/admin',
  };

  activeTab: 'document' | 'notification' = 'document';

  // Document templates
  documentTemplates: any[] = [];
  selectedDocTemplate: any = null;
  docHtml: string = '';
  docShowPreview = false;
  docSaving = false;

  // Notification templates
  notificationTemplates: any[] = [];
  selectedNotifTemplate: any = null;
  notifForm!: FormGroup;
  notifShowPreview = false;
  notifSaving = false;

  private httpService = inject(HttpServiceService);
  private alertService = inject(NgxToastrService);

  private subs: Subscription[] = [];

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.notifForm = this.fb.group({
      subject: [''],
      from_name: [''],
      from_email: ['', Validators.email],
      body: [''],
    });
    this.loadDocumentTemplates();
    this.loadNotificationTemplates();
  }

  // ── Tab ──────────────────────────────────────────────────────────────────

  setTab(tab: 'document' | 'notification') {
    this.activeTab = tab;
    this.docShowPreview = false;
    this.notifShowPreview = false;
  }

  // ── Document Templates ───────────────────────────────────────────────────

  loadDocumentTemplates() {
    const s = this.httpService.getDocumentTemplates().subscribe({
      next: (res) => {
        this.documentTemplates = res.data ?? [];
        if (this.documentTemplates.length) this.selectDocTemplate(this.documentTemplates[0]);
      },
    });
    this.subs.push(s);
  }

  selectDocTemplate(t: any) {
    this.selectedDocTemplate = t;
    this.docHtml = t.html ?? '';
    this.docShowPreview = false;
  }

  toggleDocPreview() {
    this.docShowPreview = !this.docShowPreview;
    if (this.docShowPreview) {
      setTimeout(() => this.renderDocPreview(), 50);
    }
  }

  renderDocPreview() {
    const iframe = this.docPreviewFrame?.nativeElement;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(this.docHtml);
      doc.close();
    }
  }

  saveDocTemplate() {
    if (!this.selectedDocTemplate) return;
    this.docSaving = true;
    const s = this.httpService.updateDocumentTemplate(this.selectedDocTemplate.id, { html: this.docHtml }).subscribe({
      next: (res) => {
        this.alertService.success(res.message, 'toast-top-right');
        this.selectedDocTemplate.html = this.docHtml;
        this.docSaving = false;
      },
      error: () => { this.docSaving = false; },
    });
    this.subs.push(s);
  }

  // ── Notification Templates ───────────────────────────────────────────────

  loadNotificationTemplates() {
    const s = this.httpService.getNotificationTemplates().subscribe({
      next: (res) => {
        this.notificationTemplates = res.data ?? [];
        if (this.notificationTemplates.length) this.selectNotifTemplate(this.notificationTemplates[0]);
      },
    });
    this.subs.push(s);
  }

  selectNotifTemplate(t: any) {
    this.selectedNotifTemplate = t;
    this.notifShowPreview = false;
    this.notifForm.patchValue({
      subject: t.subject ?? '',
      from_name: t.from_name ?? '',
      from_email: t.from_email ?? '',
      body: t.body ?? '',
    });
  }

  toggleNotifPreview() {
    this.notifShowPreview = !this.notifShowPreview;
    if (this.notifShowPreview) {
      setTimeout(() => this.renderNotifPreview(), 50);
    }
  }

  renderNotifPreview() {
    const iframe = this.notifPreviewFrame?.nativeElement;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(this.notifForm.value.body ?? '');
      doc.close();
    }
  }

  saveNotifTemplate() {
    if (!this.selectedNotifTemplate || this.notifForm.invalid) return;
    this.notifSaving = true;
    const s = this.httpService
      .updateNotificationTemplate(this.selectedNotifTemplate.id, this.notifForm.value)
      .subscribe({
        next: (res) => {
          this.alertService.success(res.message, 'toast-top-right');
          Object.assign(this.selectedNotifTemplate, this.notifForm.value);
          this.notifSaving = false;
        },
        error: () => { this.notifSaving = false; },
      });
    this.subs.push(s);
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }
}
